import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import clsx from 'clsx';

function Slider({
  label,
  value,
  onChange,
  color,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  color: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400">{label}</span>
        <span className={clsx('text-sm font-semibold', color)}>
          {value}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full h-1.5 bg-surface-200 rounded-full appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent
          [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-accent/30
          [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:transition-transform
          [&::-webkit-slider-thumb]:hover:scale-110"
      />
    </div>
  );
}

export default function TeamStructureStep() {
  const { teamStructure, updateTeamStructure } = useWizardStore();
  const { options, loading } = useWizardOptions();

  const teamSizes = options?.teamStructure.teamSizes ?? [];
  const claudeUserOptions = options?.teamStructure.claudeUserOptions ?? [];
  const timezoneOptions = options?.teamStructure.timezoneOptions ?? [];

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  const updateSeniority = (
    key: keyof typeof teamStructure.seniorityMix,
    value: number
  ) => {
    updateTeamStructure({
      seniorityMix: { ...teamStructure.seniorityMix, [key]: value },
    });
  };

  const toggleClaudeUser = (value: string) => {
    const current = teamStructure.claudeUsers;
    if (current.includes(value)) {
      updateTeamStructure({
        claudeUsers: current.filter((c) => c !== value),
      });
    } else {
      updateTeamStructure({ claudeUsers: [...current, value] });
    }
  };

  const totalSeniority =
    teamStructure.seniorityMix.junior +
    teamStructure.seniorityMix.mid +
    teamStructure.seniorityMix.senior +
    teamStructure.seniorityMix.lead;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          Team Structure
        </h2>
        <p className="text-sm text-gray-500">
          Help us understand your team so we can set appropriate guardrails.
        </p>
      </div>

      {/* Team Size */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Team Size
        </label>
        <select
          value={teamStructure.size}
          onChange={(e) => updateTeamStructure({ size: e.target.value })}
          className="select-field"
        >
          <option value="">Select team size...</option>
          {teamSizes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Seniority Mix */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Seniority Mix
        </label>
        <p className="text-xs text-gray-500 mb-4">
          Adjust the sliders to reflect your team composition.
          {totalSeniority !== 100 && (
            <span className="text-warning ml-2">
              Total: {totalSeniority}% (should be 100%)
            </span>
          )}
        </p>
        <div className="space-y-5 bg-surface-DEFAULT rounded-lg p-5 border border-dark-border">
          <Slider
            label="Junior"
            value={teamStructure.seniorityMix.junior}
            onChange={(v) => updateSeniority('junior', v)}
            color="text-blue-400"
          />
          <Slider
            label="Mid-level"
            value={teamStructure.seniorityMix.mid}
            onChange={(v) => updateSeniority('mid', v)}
            color="text-green-400"
          />
          <Slider
            label="Senior"
            value={teamStructure.seniorityMix.senior}
            onChange={(v) => updateSeniority('senior', v)}
            color="text-amber-400"
          />
          <Slider
            label="Lead / Principal"
            value={teamStructure.seniorityMix.lead}
            onChange={(v) => updateSeniority('lead', v)}
            color="text-purple-400"
          />

          {/* Visual bar */}
          <div className="flex h-3 rounded-full overflow-hidden bg-surface-200">
            <div
              className="bg-blue-500 transition-all duration-300"
              style={{ width: `${teamStructure.seniorityMix.junior}%` }}
            />
            <div
              className="bg-green-500 transition-all duration-300"
              style={{ width: `${teamStructure.seniorityMix.mid}%` }}
            />
            <div
              className="bg-amber-500 transition-all duration-300"
              style={{ width: `${teamStructure.seniorityMix.senior}%` }}
            />
            <div
              className="bg-purple-500 transition-all duration-300"
              style={{ width: `${teamStructure.seniorityMix.lead}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500" /> Junior
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" /> Mid
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> Senior
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-purple-500" /> Lead
            </span>
          </div>
        </div>
      </div>

      {/* Who uses Claude Code */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Who uses Claude Code?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {claudeUserOptions.map((option) => (
            <label
              key={option}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-sm',
                teamStructure.claudeUsers.includes(option)
                  ? 'bg-accent/10 border-accent/30 text-accent-300'
                  : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300'
              )}
            >
              <input
                type="checkbox"
                checked={teamStructure.claudeUsers.includes(option)}
                onChange={() => toggleClaudeUser(option)}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors',
                  teamStructure.claudeUsers.includes(option)
                    ? 'bg-accent border-accent'
                    : 'border-surface-300 bg-surface-DEFAULT'
                )}
              >
                {teamStructure.claudeUsers.includes(option) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              {option}
            </label>
          ))}
        </div>
      </div>

      {/* Dedicated Reviewers */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-surface-DEFAULT border border-dark-border">
        <div>
          <span className="text-sm font-medium text-gray-300">
            Dedicated Reviewers
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Require human review for all AI-generated code
          </p>
        </div>
        <button
          onClick={() =>
            updateTeamStructure({
              dedicatedReviewers: !teamStructure.dedicatedReviewers,
            })
          }
          className={clsx(
            'relative w-11 h-6 rounded-full transition-colors duration-200',
            teamStructure.dedicatedReviewers ? 'bg-accent' : 'bg-surface-300'
          )}
        >
          <div
            className={clsx(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
              teamStructure.dedicatedReviewers && 'translate-x-5'
            )}
          />
        </button>
      </div>

      {/* Timezone Distribution */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Timezone Distribution
        </label>
        <select
          value={teamStructure.timezones}
          onChange={(e) =>
            updateTeamStructure({ timezones: e.target.value })
          }
          className="select-field"
        >
          <option value="">Select timezone distribution...</option>
          {timezoneOptions.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
